import { initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth, type User } from "firebase/auth";
import { getAnalytics, logEvent } from "firebase/analytics";
import { APP_DOMAIN } from "./constants";
import { clearStoredCompanies, env } from "@/lib";

const config = {
  apiKey: env("VITE_FB_API_KEY"),
  authDomain: APP_DOMAIN().slice(8),
  projectId: env("VITE_FB_PROJECT"),
  storageBucket: env("VITE_FB_STORAGE_BUCKET"),
  messagingSenderId: env("VITE_FB_MESSAGING_SENDER_ID"),
  appId: env("VITE_FB_WEB_APP_ID"),
};

const app = initializeApp(config);
const auth = getAuth(app);

if (import.meta.env.VITE_USE_AUTH_EMULATOR === "true") {
  connectAuthEmulator(auth, import.meta.env.VITE_AUTH_EMULATOR_HOST ?? "http://127.0.0.1:9099", {
    disableWarnings: true,
  });
}

const analytics = getAnalytics(app);

type eventNames = "login_with_magic_link" | "login_with_sso";

const logAnalyticsEvent = (eventName: eventNames) => {
  logEvent(analytics, eventName);
};

const getAuthUser = () => {
  if (!auth.currentUser) throw new Error("User not logged in");
  return auth.currentUser;
};

// On a cold page load `auth.currentUser` is null until Firebase restores any
// persisted session. Route `beforeLoad` runs during that window, so it awaits
// this before trusting `currentUser` (e.g. to decide building access). Reads
// `currentUser` after the wait, so it is never a stale first answer.
const waitForAuthReady = async (): Promise<User | null> => {
  await auth.authStateReady();
  return auth.currentUser;
};

/**
 * Escape hatch for screens the user can get stuck on. Drops the persisted
 * company context so logging back in doesn't land on the same broken state.
 * `emailForSignIn` is deliberately kept — the login screen prefills from it.
 */
const signOutAndReload = async () => {
  // Await the sign-out: reloading first can beat Firebase clearing its
  // persisted session and drop the user back on the same stuck screen.
  try {
    await auth.signOut();
  } catch (error) {
    // Reload regardless — leaving the user stuck is worse than a stale session.
    console.error("Sign out failed, reloading anyway", error);
  }
  clearStoredCompanies();
  const baseUrl = window.location.origin + window.location.pathname;
  window.location.replace(baseUrl);
};

export {
  auth,
  getAuthUser,
  waitForAuthReady,
  logAnalyticsEvent,
  signOutAndReload,
};

// Super ugly google tag manager setup code
// eslint-disable-next-line
let dataLayer = (window as unknown as any).dataLayer;
dataLayer = dataLayer || [];
dataLayer.push("js", new Date());
dataLayer.push("config", "G-B50K74L3SY");
